import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import LoadingScreen from '../components/LoadingScreen';
import ContactModal from '../components/ContactModal';
import { useSEO } from '../utils/useSEO';
import { useLang } from '../context/LanguageContext';
import './IndustryDetail.css';
import { Helmet } from 'react-helmet-async';

const IndustryDetail = () => {
  useSEO('industries');
  const { slug } = useParams();
  const { nl } = useLang();
  const L = (obj, field) => (nl && obj?.[field + '_nl']) || obj?.[field] || '';
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUseCase, setExpandedUseCase] = useState({});
  const [activeOrgTab, setActiveOrgTab] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  
  useEffect(() => {
  fetch(`${API_BASE_URL}/api/seo/${slug}`)
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        setSeoData(data.data);
      }
    })
    .catch(err => console.error("SEO fetch error:", err));
}, [slug]);

// console.log("Slug: ", slug);

// console.log("This is : ", seoData);

  const toggleBullet = (useCaseIdx, bulletIdx) => {
    const key = `${useCaseIdx}-${bulletIdx}`;
    setExpandedUseCase(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/industries/${slug}`);
        const data = response.data.data || response.data;
        setIndustry(data);
        if (data.organizationSection?.enableTabs && data.organizationSection.tabs?.length > 0) {
          setActiveOrgTab(data.organizationSection.tabs[0].name);
        }
      } catch (error) {
        console.error('Error fetching industry:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchIndustry();
  }, [slug]);

  if (loading) return <LoadingScreen />;
  if (!industry) return <div className="not-found">Industry not found</div>;

  return (
    <div className="industry-detail">
        <Helmet>
        <title>{seoData?.title || 'Aurelia Technology'}</title>
        {seoData?.description && <meta name="description" content={seoData.description} />}
        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
        {seoData?.canonicalUrl && <link rel="canonical" href={seoData.canonicalUrl} />}
        <meta property="og:title" content={seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta property="og:description" content={seoData?.ogDescription || seoData?.description || ''} />
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData?.twitterTitle || seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta name="twitter:description" content={seoData?.twitterDescription || seoData?.ogDescription || seoData?.description || ''} />
        {(seoData?.twitterImage || seoData?.ogImage) && <meta name="twitter:image" content={seoData.twitterImage || seoData.ogImage} />}
      </Helmet>
      {/* Hero Section */}
      {industry.heroSection?.isActive && (
        <section className="industry-hero">
          <div className="hero-container">
            <div className="hero-left">
              <span className="hero-eyebrow">{L(industry.heroSection, 'eyebrow')}</span>
              <h1 className="hero-title">{L(industry.heroSection, 'title')}</h1>
              <p className="hero-description">{L(industry.heroSection, 'description')}</p>
              {industry.heroSection.buttonText && (
                <button onClick={() => setShowContactModal(true)} className="hero-btn">{L(industry.heroSection, 'buttonText')}</button>
              )}
            </div>
            {industry.heroSection.heroImage && (
              <div className="hero-right">
                <img src={industry.heroSection.heroImage} alt={L(industry.heroSection, 'title')} className="hero-image" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Highlights Section */}
      {industry.highlightsSection?.isActive && industry.highlightsSection.highlights?.length > 0 && (
        <section className="highlights-section">
          <div className="container">
            <div className="highlights-grid">
              {industry.highlightsSection.highlights.filter(h => h.isActive).map((highlight, idx) => (
                <div key={idx} className="highlight-card">
                  {highlight.logo && <img src={highlight.logo} alt="Highlight" className="highlight-logo" />}
                  <p className="highlight-text">{L(highlight, 'text')}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use Cases Section */}
      {industry.useCasesSection?.isActive && (
        <section className="use-cases-section">
          <div className="container">
            <div className="use-cases-header">
              <span className="section-eyebrow">{L(industry.useCasesSection, 'eyebrow')}</span>
              <h2 className="section-title">{L(industry.useCasesSection, 'title')}</h2>
              <p className="section-description">{L(industry.useCasesSection, 'description')}</p>
            </div>
            {industry.useCasesSection.useCases?.length > 0 && (
              <div className="use-cases-grid">
                {industry.useCasesSection.useCases.filter(uc => uc.isActive).map((useCase, idx) => (
                  <div key={idx} className="use-case-group">
                    <h3 className="use-case-group-title">{L(useCase, 'title')}</h3>
                    {useCase.bullets?.length > 0 && (
                      <div className="use-case-accordion">
                        {useCase.bullets.filter(b => b.isActive).map((bullet, bidx) => (
                          <div key={bidx} className="accordion-item">
                            <button className="accordion-header" onClick={() => toggleBullet(idx, bidx)}>
                              <span>{L(bullet, 'text')}</span>
                              <span className={`accordion-icon ${expandedUseCase[`${idx}-${bidx}`] ? 'expanded' : ''}`}>+</span>
                            </button>
                            {expandedUseCase[`${idx}-${bidx}`] && (
                              <div className="accordion-content">
                                <p>{L(bullet, 'description')}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Organizations Section */}
      {industry.organizationSection?.isActive && (
        <section className="organizations-section">
          <div className="container">
            {industry.organizationSection.enableTabs && industry.organizationSection.tabs?.length > 0 ? (
              <>
                <div className="org-tabs">
                  {industry.organizationSection.tabs.map((tab, idx) => (
                    <button 
                      key={idx}
                      className={`org-tab ${activeOrgTab === tab.name ? 'active' : ''}`}
                      onClick={() => setActiveOrgTab(tab.name)}
                    >
                      {L(tab, 'name')}
                    </button>
                  ))}
                </div>
                <div className="org-tab-content">
                  {activeOrgTab && industry.organizationSection.tabs.filter(tab => tab.name === activeOrgTab).map((tab, idx) => (
                      <div key={idx} className="section-header">
                        <span className="section-eyebrow">{L(tab, 'eyebrow')}</span>
                        <h2 className="section-title">{L(tab, 'title')}</h2>
                        <p className="section-description">{L(tab, 'description')}</p>
                      </div>
                  ))}
                  {industry.organizationSection.organizations?.length > 0 && (
                    <div className="organizations-grid">
                      {industry.organizationSection.organizations
                        .filter(org => org.isActive && org.category === activeOrgTab)
                        .map((org, idx) => (
                          <div key={idx} className="organization-card">
                            {org.logo && <img src={org.logo} alt={L(org, 'name')} className="org-logo" />}
                            <h4 className="org-name">{L(org, 'name')}</h4>
                            {org.description && <p className="org-description">{L(org, 'description')}</p>}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="section-header">
                  <span className="section-eyebrow">{L(industry.organizationSection, 'eyebrow')}</span>
                  <h2 className="section-title">{L(industry.organizationSection, 'title')}</h2>
                  <p className="section-description">{L(industry.organizationSection, 'description')}</p>
                </div>
                {industry.organizationSection.organizations?.length > 0 && (
                  <div className="organizations-grid">
                    {industry.organizationSection.organizations.filter(org => org.isActive).map((org, idx) => (
                      <div key={idx} className="organization-card">
                        {org.logo && <img src={org.logo} alt={L(org, 'name')} className="org-logo" />}
                        <h4 className="org-name">{L(org, 'name')}</h4>
                        {org.description && <p className="org-description">{L(org, 'description')}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Success Section */}
      {industry.successSection?.isActive && industry.successSection.cards?.length > 0 && (
        <section className="success-section">
          <div className="container">
            <div className="section-header">
              <span className="section-eyebrow">{L(industry.successSection, 'eyebrow')}</span>
              <h2 className="section-title">{L(industry.successSection, 'title')}</h2>
            </div>
            <div className="success-grid">
              {industry.successSection.cards.filter(card => card.isActive).map((card, idx) => (
                <div key={idx} className="success-card">
                  {card.logo && <img src={card.logo} alt="Logo" className="success-logo" />}
                  {card.image && <img src={card.image} alt={L(card, 'title')} className="success-image" />}
                  <h3 className="success-title">{L(card, 'title')}</h3>
                  {card.link && <Link to={card.link} className="success-link">{L(card, 'linkText')}</Link>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Resources Section */}
      {industry.resourcesSection?.isActive && industry.resourcesSection.cards?.length > 0 && (
        <section className="resources-section">
          <div className="container">
            <div className="section-header">
              <span className="section-eyebrow">{L(industry.resourcesSection, 'eyebrow')}</span>
              <h2 className="section-title">{L(industry.resourcesSection, 'title')}</h2>
            </div>
            <div className="resources-grid">
              {industry.resourcesSection.cards.filter(card => card.isActive).map((card, idx) => (
                <div key={idx} className="resource-card">
                  <span className="resource-tag">{L(card, 'tag')}</span>
                  <h3 className="resource-title">{L(card, 'title')}</h3>
                  {card.link && <Link to={card.link} className="resource-cta">{L(card, 'cta')}</Link>}
                </div>
              ))}
            </div>
            {industry.resourcesSection.buttonText && (
              <div className="resources-footer">
                <Link to={industry.resourcesSection.buttonLink} className="resources-btn">{L(industry.resourcesSection, 'buttonText')}</Link>
              </div>
            )}
          </div>
        </section>
      )}
      
      <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
    </div>
  );
};

export default IndustryDetail;
